import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Spin, Collapse, Breadcrumb } from "antd";
import Swal from "sweetalert2";
import { all_routes } from "../../router/all_routes";
import {
  getLeaveRequestById,
  createLeaveRequest,
  getEmpLeaveFileAttachment,
  getLeaveDropDown,
} from "../../../core/data/redux/leaveSlice";
import { tokenService } from "../../../core/utils/tokenService";
import {
  viewLeaveAttachmentFile,
  parseEmpLeaveAttachmentList,
  normalizeLeaveAttachmentForUi,
  LEAVE_ADDITIONAL_FIELDS_HIDDEN_KEYS,
  KNOWN_LEAVE_FIELD_LABELS,
  absenceTypeIndicatesRmddaDischarge,
} from "../LeaveManagement/leaveUtils";
import "./LeaveApproval.scss";

const { Panel } = Collapse;

// ── Static Helpers ────────────────────────────────────────────────────────────

const formatDateTime = (iso: string): string => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy}, ${hh}:${min}`;
  } catch {
    return iso;
  }
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr || dateStr === "N/A" || dateStr === "-") return "-";
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mIdx = parseInt(month, 10) - 1;
    return `${day} ${months[mIdx] || month} ${year}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mIdx = parseInt(month, 10) - 1;
    return `${day} ${months[mIdx] || month} ${year}`;
  }
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const mapStatusLabel = (code: string): string => {
  const s = (code ?? "").toString().toUpperCase().trim();
  const labels: Record<string, string> = {
    SENT: "Pending Approval",
    T: "To Be Approved",
    NEW: "New",
    P: "Partially Approved",
    A: "Approved",
    APPROVED: "Approved",
    R: "Rejected",
    REJECTED: "Rejected",
    D: "Processing Complete",
    X: "Deleted",
  };
  return labels[s] ?? (code || "Pending Approval");
};

const getStatusConfig = (code: string) => {
  const s = (code ?? "").toUpperCase();
  const label = mapStatusLabel(code);
  if (["A", "APPROVED"].includes(s))
    return { color: "#16A34A", bg: "#f0fdf4", border: "#bbf7d0", icon: "ti ti-circle-check", label };
  if (["R", "REJECTED", "X", "DELETED"].includes(s))
    return { color: "#DC2626", bg: "#fef2f2", border: "#fecaca", icon: "ti ti-circle-x", label };
  if (["SENT", "T", "NEW", "TO BE APPROVED"].includes(s))
    return { color: "#EA580C", bg: "#fff7ed", border: "#fed7aa", icon: "ti ti-clock", label };
  return { color: "#087990", bg: "#e6f8fa", border: "#b6effb", icon: "ti ti-info-circle", label };
};

// ── UI Components matching ReimbursementApprovalDetailPage ───────────────────

const InfoRow = ({
  label,
  value,
  highlight = false,
  colClass = "col-12 col-sm-6 col-md-4 col-lg-3",
}: {
  label: string;
  value?: React.ReactNode;
  highlight?: boolean;
  colClass?: string;
}) => (
  <div className={`${colClass} px-3 d-flex flex-column gap-1 mb-3`}>
    <span className="info-row-lbl">{label}</span>
    <span className={`info-row-val ${highlight ? "hl" : ""}`}>{value ?? "-"}</span>
  </div>
);

const PanelHdr = ({ icon, title }: { icon: string; title: string }) => (
  <div className="d-flex align-items-center gap-2">
    <i className={`${icon} text-dark`} style={{ fontSize: "20px" }} />
    <span className="fw-bold text-dark" style={{ fontSize: "16px" }}>
      {title}
    </span>
  </div>
);

const EmptyMsg: React.FC<{ text: string }> = ({ text }) => (
  <div className="text-center py-3 text-muted small">
    <i className="ti ti-info-circle me-1" />
    {text}
  </div>
);

const RemarkRow: React.FC<{
  name: string;
  userId?: string;
  text: string;
  dateTime: string;
  isLast: boolean;
}> = ({ name, userId, text, dateTime, isLast }) => (
  <div className="d-flex gap-3 mb-3" style={{ position: "relative" }}>
    {!isLast && <div className="remark-line" style={{ top: 38 }} />}
    <div className="remark-avatar">{(name ?? userId ?? "?")[0]?.toUpperCase()}</div>
    <div className="flex-grow-1 rounded-2 p-3" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
      <div className="d-flex justify-content-between flex-wrap gap-1 mb-1">
        <span className="fw-bold text-dark small">
          {name || "-"}
          {userId && (
            <span className="text-muted ms-2" style={{ fontSize: 11 }}>
              ({userId})
            </span>
          )}
        </span>
        {dateTime && (
          <span className="text-muted" style={{ fontSize: 11 }}>
            <i className="ti ti-clock me-1" />
            {dateTime}
          </span>
        )}
      </div>
      <p className="mb-0 text-secondary small" style={{ fontStyle: "italic", lineHeight: 1.5 }}>
        {text || "—"}
      </p>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const LeaveApprovalPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const routes = all_routes;

  const [comments, setComments] = useState("");
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Redux state
  const { leaveRequestById: rawLeaveData, leaveDropDown, loading } = useSelector(
    (state: any) => state.leavesState || {}
  );
  const authUser = useSelector(
    (state: any) => state.employeeAuthState?.user || state.authState?.user
  );

  const leaveData = rawLeaveData?.data || rawLeaveData;
  const employeeDetails = leaveData?.employee_details;

  // Determine logged-in approver employee ID (guaranteeing exact 8-digit padding matching SAP)
  const approverEmpId = useMemo(() => {
    // 1. Try decoding directly from JWT Bearer token
    try {
      const token =
        tokenService.getToken() ||
        sessionStorage.getItem("authorization") ||
        localStorage.getItem("authorization");
      if (token) {
        const cleanToken = token.replace(/^Bearer\s+/i, "");
        const parts = cleanToken.split(".");
        if (parts.length >= 2) {
          const payload = JSON.parse(atob(parts[1]));
          const jwtEmp = payload?.empId || payload?.employeeId;
          if (jwtEmp) {
            const str = String(jwtEmp).trim();
            return /^\d+$/.test(str) ? str.padStart(8, "0") : str;
          }
        }
      }
    } catch {}

    // 2. Try authUser from Redux
    const reduxEmp = authUser?.empId || authUser?.employee_id || authUser?.data?.user?.empId;
    if (reduxEmp) {
      const str = String(reduxEmp).trim();
      return /^\d+$/.test(str) ? str.padStart(8, "0") : str;
    }

    // 3. Try localStorage user
    try {
      const raw = localStorage.getItem("user");
      if (raw && raw !== "undefined") {
        const u = JSON.parse(raw);
        const uEmp = u?.empId || u?.employee_id || u?.usrid;
        if (uEmp) {
          const str = String(uEmp).trim();
          return /^\d+$/.test(str) ? str.padStart(8, "0") : str;
        }
      }
    } catch {}

    // 4. Try ApproverUserID from leaveData
    const approverId = leaveData?.ApproverUserID || leaveData?.ApproverID || leaveData?.ApproverPernr;
    if (approverId) {
      const str = String(approverId).trim();
      return /^\d+$/.test(str) ? str.padStart(8, "0") : str;
    }

    return "";
  }, [authUser, leaveData?.ApproverUserID, leaveData?.ApproverID, leaveData?.ApproverPernr]);

  // Dropdown categories for Additional Fields translation
  const dropdownBuckets = useMemo(() => {
    const rawList: any[] = Array.isArray(leaveDropDown?.data)
      ? leaveDropDown.data
      : Array.isArray(leaveDropDown?.data?.data)
      ? leaveDropDown.data.data
      : [];
    return {
      customer02: rawList.filter((i) => i.dd === "Customer02"),
      customer09: rawList.filter((i) => i.dd === "Customer09"),
      customer10: rawList.filter((i) => i.dd === "Customer10"),
    };
  }, [leaveDropDown]);

  // Fetch initial request details & dropdowns
  useEffect(() => {
    if (id) {
      dispatch(getLeaveRequestById({ requestID: id }));
    }
    dispatch(getLeaveDropDown({}) as any);
  }, [id, dispatch]);

  // Fetch file attachments
  useEffect(() => {
    const rawEmp = leaveData?.EmployeeID ?? employeeDetails?.empId ?? "";
    const empId = String(rawEmp || "").trim().padStart(8, "0");
    const leaveReqId = String(leaveData?.RequestID ?? id ?? "").trim();

    if (empId && leaveReqId && !/^0+$/.test(empId)) {
      setLoadingAttachments(true);
      dispatch(getEmpLeaveFileAttachment({ empId, leaveRequestId: leaveReqId }))
        .unwrap()
        .then((res: any) => {
          const rawList = parseEmpLeaveAttachmentList(res);
          const normalized = rawList.map(normalizeLeaveAttachmentForUi);
          setAttachments(normalized);
        })
        .catch(() => setAttachments([]))
        .finally(() => setLoadingAttachments(false));
    }
  }, [dispatch, leaveData?.EmployeeID, employeeDetails?.empId, leaveData?.RequestID, id]);


  // Working & Absence Hours calculation
  const absenceHoursDisplay = useMemo(() => {
    const hoursSource =
      leaveData?.WorkingHoursDuration !== undefined &&
      leaveData?.WorkingHoursDuration !== null &&
      String(leaveData?.WorkingHoursDuration).trim() !== ""
        ? String(leaveData.WorkingHoursDuration).trim()
        : String(leaveData?.ABRST ?? "").trim();
    if (!hoursSource) return "-";
    const parsed = parseFloat(hoursSource);
    return !isNaN(parsed) ? `${parsed.toFixed(2)} Hrs` : hoursSource;
  }, [leaveData?.WorkingHoursDuration, leaveData?.ABRST]);

  // Additional Fields display list (filtered & translated identical to mobile)
  const additionalFieldsDisplay = useMemo(() => {
    if (!leaveData) return [];
    const merged: Record<string, any> = {
      ...(leaveData.AdditionalFields || {}),
    };

    if (!merged.CUSTOMER01 && leaveData.CUSTOMER01) merged.CUSTOMER01 = leaveData.CUSTOMER01;
    if (!merged.CUSTOMER02 && leaveData.CUSTOMER02) merged.CUSTOMER02 = leaveData.CUSTOMER02;
    if (!merged.CUSTOMER03 && leaveData.CUSTOMER03) merged.CUSTOMER03 = leaveData.CUSTOMER03;
    if (!merged.CUSTOMER05 && leaveData.CUSTOMER05) merged.CUSTOMER05 = leaveData.CUSTOMER05;
    if (!merged.CUSTOMER06 && leaveData.CUSTOMER06) merged.CUSTOMER06 = leaveData.CUSTOMER06;
    if (!merged.CUSTOMER07 && leaveData.CUSTOMER07) merged.CUSTOMER07 = leaveData.CUSTOMER07;
    if (!merged.CUSTOMER08 && leaveData.CUSTOMER08) merged.CUSTOMER08 = leaveData.CUSTOMER08;
    if (!merged.CUSTOMER09 && leaveData.CUSTOMER09) merged.CUSTOMER09 = leaveData.CUSTOMER09;
    if (!merged.CUSTOMER10 && leaveData.CUSTOMER10) merged.CUSTOMER10 = leaveData.CUSTOMER10;
    if (!merged.RMDDA && leaveData.RMDDA) merged.RMDDA = leaveData.RMDDA;

    return Object.entries(merged)
      .filter(([key, value]) => {
        if (LEAVE_ADDITIONAL_FIELDS_HIDDEN_KEYS.includes(key)) return false;
        if (value === null || value === undefined || value === "") return false;
        return true;
      })
      .map(([key, value]) => {
        let displayVal = String(value);

        if (key === "CUSTOMER08") {
          displayVal = value === true || value === "true" || value === "X" ? "Yes" : "No";
        } else if (key === "CUSTOMER02") {
          const match = dropdownBuckets.customer02.find((i) => i.key === String(value));
          displayVal = match ? match.value : String(value);
        } else if (key === "CUSTOMER09") {
          const match = dropdownBuckets.customer09.find((i) => i.key === String(value));
          displayVal = match ? match.value : String(value);
        } else if (key === "CUSTOMER10") {
          const match = dropdownBuckets.customer10.find((i) => i.key === String(value));
          displayVal = match ? match.value : String(value);
        } else if (key === "RMDDA") {
          displayVal = formatDate(String(value));
        } else if (key === "ABRST") {
          displayVal = absenceHoursDisplay;
        }

        let label = KNOWN_LEAVE_FIELD_LABELS[key] || key;
        if (key === "RMDDA") {
          label = absenceTypeIndicatesRmddaDischarge(leaveData?.AbsenceTypeCode, leaveData?.AbsenceTypeName)
            ? "Discharge Date"
            : "Expected Due Date";
        }

        return {
          key,
          label,
          value: displayVal,
        };
      });
  }, [leaveData, dropdownBuckets, absenceHoursDisplay]);

  // Remarks list (matches mobile requestDetails.remarks)
  const remarksList = useMemo(() => {
    if (Array.isArray(leaveData?.remarks) && leaveData.remarks.length > 0) {
      return leaveData.remarks;
    }
    return [];
  }, [leaveData]);

  const employeeFullName = useMemo(() => {
    const fn = employeeDetails?.firstname || "";
    const ln = employeeDetails?.lastname || employeeDetails?.lsastname || "";
    const combined = `${fn} ${ln}`.trim();
    return combined || leaveData?.EmployeeName || "Employee";
  }, [employeeDetails, leaveData?.EmployeeName]);

  // Action submission handler matching mobile approveRejectLeave exactly
  const handleAction = async (actionCode: number) => {
    if (!comments.trim()) {
      Swal.fire({
        title: "Remarks Required",
        text: "Please enter your remarks before submitting.",
        icon: "warning",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3500,
      });
      return;
    }

    const actionText = actionCode === 4 ? "Approve" : "Reject";
    const result = await Swal.fire({
      title: `${actionText} Leave?`,
      text: `Do you want to ${actionText.toLowerCase()} this leave request?`,
      icon: actionCode === 4 ? "question" : "warning",
      showCancelButton: true,
      confirmButtonColor: actionCode === 4 ? "#16a34a" : "#dc2626",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, ${actionText}!`,
    });

    if (result.isConfirmed) {
      setSubmitting(true);
      // Payload matching mobile LeaveApplication.tsx exactly
      const payload = {
        EmployeeID: approverEmpId || "",
        RequestID: id || leaveData?.RequestID,
        ActionCode: actionCode, // 4 for Approve, 5 for Reject
        Notes: comments.trim() || "",
        LeaveKey: leaveData?.LeaveKey || "",
        ProcessCheckOnlyInd: false,
        ChangeStateID: leaveData?.ChangeStateID || 1,
        StartDate: "00-00-0000",
        EndDate: "00-00-0000",
      };

      try {
        const response = await dispatch(createLeaveRequest(payload)).unwrap();
        if (response.status === 200 || response.status === 201 || response.success) {
          Swal.fire({
            title: "Success",
            text: `Leave request ${actionText.toLowerCase()}ed successfully.`,
            icon: "success",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
          navigate(routes.todoWorklist.tasks);
        } else {
          throw new Error(response.message || `Failed to ${actionText.toLowerCase()} request`);
        }
      } catch (err: any) {
        Swal.fire({
          title: "Error",
          text: typeof err === "string" ? err : err?.message || "An error occurred during submission.",
          icon: "error",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 5000,
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Status configuration
  const statusCfg = getStatusConfig(leaveData?.StatusCode || leaveData?.StatusName || "");

  if (loading === "pending" && !leaveData) {
    return (
      <div className="page-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <Spin size="large" tip="Loading leave details…" />
      </div>
    );
  }

  if (!leaveData && loading !== "pending") {
    return (
      <div className="page-wrapper d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <i className="ti ti-file-off fs-1 text-muted opacity-25 mb-3" />
        <h6 className="text-muted mb-3">Leave record not found</h6>
        <button className="btn btn-sm btn-outline-secondary px-4 rounded-2" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="page-wrapper" style={{ backgroundColor: "#f4f5f7", minHeight: "100vh" }}>
      {/* Background operation spinner */}
      {submitting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      )}

      <div className="content">
        {/* ── Page Header & Breadcrumb ─────────────────────────────────── */}
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <div>
            <h5 className="fw-bold text-dark mb-0">Leave Approval Request</h5>
            <Breadcrumb
              style={{ fontSize: 11, marginTop: 4 }}
              items={[
                { title: <Link to={routes.adminDashboard} className="text-muted">Home</Link> },
                { title: <Link to={routes.todoWorklist.tasks} className="text-muted">Universal Worklist</Link> },
                { title: <span style={{ color: "#ab1f24" }}>Leave Approval</span> },
              ]}
            />
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm px-3 rounded-2" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Back
            </button>
          </div>
        </div>

        {/* ── Status Banner (identical to ReimbursementApprovalDetailPage) ── */}
        <div
          className="card border-0 rounded-2 mb-4"
          style={{
            background: statusCfg.bg,
            border: `1px solid ${statusCfg.border}`,
            borderLeft: `4px solid ${statusCfg.color}`,
          }}
        >
          <div className="card-body py-3 px-4 d-flex align-items-center gap-3">
            <i className={`${statusCfg.icon}`} style={{ color: statusCfg.color, fontSize: "32px" }} />
            <div>
              <div className="text-muted fw-bold mb-0" style={{ fontSize: 10, letterSpacing: 1 }}>
                REQUEST STATUS
              </div>
              <h5 className="fw-bold mb-0" style={{ color: statusCfg.color }}>
                {statusCfg.label}
              </h5>
            </div>
          </div>
        </div>

        {/* ── Application Info Card ────────────────────────────────────── */}
        <div className="card border shadow-sm rounded-2 mb-4">
          <div className="card-header bg-light border-bottom py-3 px-4 d-flex align-items-center gap-2">
            <i className="ti ti-file-text text-dark" style={{ fontSize: "22px" }} />
            <h5 className="fw-bold text-dark mb-0" style={{ fontSize: "18px" }}>
              Application Info
            </h5>
          </div>
          <div className="card-body px-4 py-4">
            <div className="row g-4">
              <InfoRow
                label="Reference No."
                colClass="col-12 col-sm-6"
                value={
                  <span className="font-monospace text-break" style={{ color: "#ab1f24", fontWeight: 700 }}>
                    {leaveData?.RequestID || id || "-"}
                  </span>
                }
              />
              <InfoRow
                label="Applied Date"
                colClass="col-12 col-sm-6"
                value={formatDate(leaveData?.created_at || leaveData?.FirstSubmissionDate)}
              />
            </div>
          </div>
        </div>

        {/* ── Collapsible Sections (Reimbursement accordion style) ────── */}
        <Collapse
          className="reimb-details-collapse mb-4"
          bordered={false}
          expandIconPosition="end"
          defaultActiveKey={["employee", "leave", "remarks", "additional", "attachments"]}
          style={{ background: "transparent", display: "flex", flexDirection: "column", gap: 12 }}
        >
          {/* ── Panel: Employee Details (department, name, DOJ only) ── */}
          <Panel
            key="employee"
            header={<PanelHdr icon="ti ti-user" title="Employee Details" />}
            className="reimb-panel shadow-sm rounded-2"
          >
            <div className="row g-0">
              <InfoRow label="Name" value={employeeFullName} colClass="col-12 col-sm-4" />
              <InfoRow label="Department" value={employeeDetails?.department || "N/A"} colClass="col-12 col-sm-4" />
              <InfoRow
                label="DOJ"
                value={formatDate(
                  employeeDetails?.dateOfJoining ||
                    employeeDetails?.doj ||
                    employeeDetails?.date_of_joining ||
                    employeeDetails?.joiningDate ||
                    employeeDetails?.DOJ ||
                    leaveData?.DOJ ||
                    leaveData?.dateOfJoining ||
                    leaveData?.date_of_joining
                )}
                colClass="col-12 col-sm-4"
              />
            </div>
          </Panel>

          {/* ── Panel: Leave Details ─────────────────────────────────── */}
          <Panel
            key="leave"
            header={<PanelHdr icon="ti ti-calendar" title="Leave Details" />}
            className="reimb-panel shadow-sm rounded-2"
          >
            <div className="row g-0">
              <InfoRow
                label="Leave Type"
                value={leaveData?.AbsenceTypeName || leaveData?.AbsenceTypeCode || "Casual Leave"}
                highlight
                colClass="col-12 col-sm-4"
              />
              <InfoRow label="Start Date" value={formatDate(leaveData?.StartDate)} colClass="col-12 col-sm-4" />
              <InfoRow label="End Date" value={formatDate(leaveData?.EndDate)} colClass="col-12 col-sm-4" />
              <InfoRow
                label="Duration"
                value={`${parseFloat(leaveData?.WorkingDaysDuration || "0").toFixed(1)} Days`}
                colClass="col-12 col-sm-4"
              />
            </div>
          </Panel>

          {/* ── Panel: Last Note / Previous Remarks ──────────────────── */}
          <Panel
            key="remarks"
            header={<PanelHdr icon="ti ti-message-dots" title="Last Note / Remarks" />}
            className="reimb-panel shadow-sm rounded-2"
          >
            {remarksList.length === 0 ? (
              <EmptyMsg text="No previous approver remarks available." />
            ) : (
              remarksList.map((item: any, idx: number) => {
                const dateStr =
                  item?.createdAt || item?.updatedAt || item?.createdDate
                    ? formatDateTime(item.createdAt || item.updatedAt || item.createdDate)
                    : "";
                return (
                  <RemarkRow
                    key={idx}
                    name={item?.Name || item?.employeeName || "Approver"}
                    userId={item?.empId || item?.approverId}
                    text={item?.NoticeText || item?.remarkText || "No notes"}
                    dateTime={dateStr}
                    isLast={idx === remarksList.length - 1}
                  />
                );
              })
            )}
          </Panel>

          {/* ── Panel: Additional Details ────────────────────────────── */}
          {additionalFieldsDisplay.length > 0 && (
            <Panel
              key="additional"
              header={<PanelHdr icon="ti ti-list" title="Additional Details" />}
              className="reimb-panel shadow-sm rounded-2"
            >
              <div className="row g-0">
                {additionalFieldsDisplay.map((field) => (
                  <InfoRow key={field.key} label={field.label} value={field.value} />
                ))}
              </div>
            </Panel>
          )}

          {/* ── Panel: Uploaded Documents ────────────────────────────── */}
          <Panel
            key="attachments"
            header={<PanelHdr icon="ti ti-paperclip" title="Uploaded Documents" />}
            className="reimb-panel shadow-sm rounded-2"
          >
            {loadingAttachments ? (
              <div className="text-center py-3">
                <Spin size="small" />
                <span className="ms-2 text-muted small">Loading attachments...</span>
              </div>
            ) : attachments.length > 0 || (leaveData?.Attachments && leaveData.Attachments.length > 0) ? (
              <div className="d-flex flex-column gap-2">
                {(attachments.length > 0 ? attachments : leaveData.Attachments).map((doc: any, ai: number) => {
                  const fileName =
                    doc?.FileName ?? doc?.fileName ?? doc?.name ?? doc?.file_name ?? `Attachment-${ai + 1}`;
                  const fileExt = (
                    doc?.FileExtension ??
                    doc?.fileExtension ??
                    (fileName.includes(".") ? fileName.split(".").pop() : "") ??
                    ""
                  ).toLowerCase();
                  const ext = fileExt.replace(/^\./, "");
                  const fileUrl =
                    doc?.url ||
                    doc?.Content ||
                    doc?.FileContent ||
                    doc?.attachmentUrl ||
                    doc?.AttachmentUrl ||
                    "";

                  const icon =
                    ext === "pdf"
                      ? "ti ti-file-type-pdf text-danger"
                      : ["jpg", "jpeg", "png", "webp", "gif"].includes(ext)
                      ? "ti ti-photo text-primary"
                      : ["doc", "docx"].includes(ext)
                      ? "ti ti-file-type-doc text-primary"
                      : ["xls", "xlsx", "csv"].includes(ext)
                      ? "ti ti-file-type-xls text-success"
                      : "ti ti-file text-muted";

                  return (
                    <div
                      key={ai}
                      className="d-flex align-items-center gap-3 p-3 rounded-2 border"
                      role="button"
                      tabIndex={0}
                      style={{ background: "#f8f9fa", cursor: fileUrl ? "pointer" : "default" }}
                      onClick={() => fileUrl && viewLeaveAttachmentFile(fileUrl)}
                      onKeyDown={(e) => e.key === "Enter" && fileUrl && viewLeaveAttachmentFile(fileUrl)}
                    >
                      <i className={`${icon}`} style={{ fontSize: "24px" }} />
                      <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-semibold small text-dark text-truncate">{fileName}</div>
                        {doc?.fileSize && (
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            {doc.fileSize} KB
                          </div>
                        )}
                      </div>
                      {fileUrl && (
                        <i className="ti ti-external-link text-muted" style={{ fontSize: "20px" }} />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyMsg text="No documents attached." />
            )}
          </Panel>
        </Collapse>

        {/* ── Approver Actions Card ────────────────────────────────────── */}
        <div className="card border shadow-sm rounded-2 mb-4">
          <div className="card-header bg-light border-bottom py-3 px-4 d-flex align-items-center gap-2">
            <i className="ti ti-check-circle text-dark" style={{ fontSize: "22px" }} />
            <h5 className="fw-bold text-dark mb-0" style={{ fontSize: "18px" }}>
              Approver Remarks
            </h5>
          </div>
          <div className="card-body px-4 pt-4 pb-4">
            <div className="mb-3">
              <label className="form-label small fw-semibold text-muted">
                Remarks <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Enter your remarks here..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <button
                className="btn btn-danger px-4 rounded-2"
                onClick={() => handleAction(5)}
                disabled={submitting}
              >
                <i className="ti ti-x me-1" /> Reject
              </button>
              <button
                className="btn btn-success px-4 rounded-2"
                onClick={() => handleAction(4)}
                disabled={submitting}
              >
                <i className="ti ti-check me-1" /> Approve
              </button>
            </div>
          </div>
        </div>

        <div style={{ height: 40 }} />
      </div>

      {/* Scoped CSS overrides matching ReimbursementApprovalDetailPage styling */}
      <style>{`
        .reimb-details-collapse .ant-collapse-item {
            background: #ffffff;
            border: 1px solid #e5e7eb !important;
            border-radius: 6px !important;
            margin-bottom: 12px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        .reimb-details-collapse .ant-collapse-header {
            padding: 12px 20px !important;
            background: #f8f9fa;
            font-weight: 600;
            color: #343a40 !important;
            align-items: center;
            border-radius: 6px 6px 0 0 !important;
        }
        .reimb-details-collapse .ant-collapse-content { 
            border-top: 1px solid #dee2e6; 
        }
        .reimb-details-collapse .ant-collapse-content-box { 
            padding: 20px !important; 
        }
        .reimb-panel { border: none !important; }
        
        .info-row-lbl { font-size: 13px; color: #6c757d; font-weight: 500; }
        .info-row-val { font-size: 14px; color: #212529; font-weight: 500; }
        .info-row-val.hl { color: #ab1f24; font-weight: 600; }
        
        .remark-line { position: absolute; left: 19px; top: 0; bottom: 0; width: 2px; background: #dee2e6; z-index: 0; }
        .remark-avatar { 
            width: 40px; height: 40px; border-radius: 6px; background: #6c757d; color: #fff;
            display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 16px;
            flex-shrink: 0; position: relative; z-index: 1; 
        }
      `}</style>
    </div>
  );
};

export default LeaveApprovalPage;
